import {useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef} from 'react';
import classNames from 'classnames';
import {getMorphoService, serviceNames} from '@/rte/editor';
import {feLogger, returnTopBtn} from '@/buried';
import ReturnTopIcon from '@/assets/svg/returnTop.svg?tsx';
import {DEMO_MODE_TYPE} from '@/constants/document/doc';
import Tooltip from '@/components/Tooltip';
import * as store from '@/store';
import ReactDOM from 'react-dom';
import eventBus, {EDITOR_DOM_READY} from '@/eventBus';

import styles from './index.module.scss';

interface IShowReturnTop {
    screenHeight: number;
    scrollTop: number;
}

interface Iprops {
    mode?: string;
    demoMode?: boolean;
    demoModeType?: string;
}

const ReturnTopModule = forwardRef((props:Iprops, ref: any) => {
    const {mode, demoMode, demoModeType} = props;

    const [visible, setVisible] = useState(false);
    // 计算是否显示返回顶部按钮
    const isShowReturnTop = (data: IShowReturnTop) => {
        if (!data) {
            return;
        }
        const {scrollTop, screenHeight} = data;
        if (screenHeight) {
            const screens = Math.floor(scrollTop / screenHeight);
            // 滚动超过2屏时，显示返回顶部按钮,小于2屏隐藏返回顶部按钮
            setVisible(screens > 1);
        }
    };

    useImperativeHandle(ref, () => ({
        isShowReturnTop,
    }));

    const checkReturnTop = useCallback(() => {
        try {
            const navigationService = getMorphoService(serviceNames.INavigationService);
            if (navigationService && typeof navigationService.getScrollInfo === 'function') {
                // 文档状态切换时，重新计算是否显示返回顶部按钮
                const scrollInfo = navigationService.getScrollInfo();
                if (scrollInfo) {
                    isShowReturnTop(scrollInfo);
                } else {
                    setVisible(false);
                }
            }
        }
        catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        // 文档初始化时，计算是否显示返回顶部按钮
        eventBus.on(EDITOR_DOM_READY, checkReturnTop);
        return () => {
            eventBus.off(EDITOR_DOM_READY, checkReturnTop);
        };
    }, [checkReturnTop]);

    useEffect(() => {
        // 文档模式切换时，先隐藏返回顶部按钮
        setVisible(false);
        // 文档模式切换时，重新计算是否显示返回顶部按钮
        // 延迟执行，防止页面切换时，编辑器还没初始化完成
        setTimeout(() => {
            checkReturnTop();
        }, 0);
    }, [mode, demoMode, demoModeType, checkReturnTop]);

    // 点击返回顶部
    const handleReturnTop = () => {
        try {
            const navigationService = getMorphoService(serviceNames.INavigationService);
            // 调用编辑器返回顶部方法
            if (navigationService && typeof navigationService.locateToTop === 'function') {
                navigationService.locateToTop();
            }
            setVisible(false);
            // 点击回顶埋点
            const curDocument = store.getCurDocument();
            feLogger(returnTopBtn({
                page_id: curDocument!.docGuid,
                user_id: curDocument!.userGuid,
            }));
        }
        catch (e) {
            console.error(e);
        }
    };

    // 演示模式卡片视图不支持一键回顶
    const showVisible = demoMode && demoModeType === DEMO_MODE_TYPE.page ? false : visible;

    return (<>
        {
            showVisible && (
                <Tooltip
                    content={'返回顶部'}
                    placement={'top'}
                    className={styles.tooltipWrapper}
                >
                    <div
                        className={styles.returnTop}
                        onClick={handleReturnTop}
                    >
                        <ReturnTopIcon className={styles.icon} />
                    </div>
                </Tooltip>
            )
        }
    </>
    );
});
export const ReturnTop = ReturnTopModule;

export const useReturnTop = (mode?: string, demoMode?: boolean, demoModeType?: string) => {
    const innerRef = useRef(null);
    const isShowReturnTop = (data: IShowReturnTop) => {
        // @ts-ignore
        if (innerRef.current && typeof innerRef.current.isShowReturnTop === 'function') {
            // @ts-ignore
            innerRef.current.isShowReturnTop(data);
        }
    };

    const ReturnTopNode = () => {
        const returnTopNode = document.getElementById('returnTop');
        if (!returnTopNode) {
            return null;
        }

        return ReactDOM.createPortal(
            <ReturnTop
                ref={innerRef}
                mode={mode}
                demoMode={demoMode}
                demoModeType={demoModeType}
            />,
            returnTopNode
        );
    };

    return {ReturnTopNode, isShowReturnTop};
};
